/**
 * Générateur — Traduire la relation de dépendance entre deux grandeurs par un
 * tableau de valeurs à partir d'une formule.
 * Attendu ID : 993 | Niveau : 5ème | Domaine : Fonctions (cluster 2, O13)
 *
 * Plan de cartes validé (doc prof TANOED EXERCISEUR, Fonctions cluster 2) :
 *   - carre (Ex.1 « Périmètre et aire du carré », le contraste P/A) :
 *       carre_tableau_trous_saisie, carre_texte, carre_qcm_unique, carre_vf
 *   - formule (Ex.3 cylindre v = r²πh + Ex.4 rectangles S = kL) :
 *       formule_qcm_unique, formule_trou_drag (valeurs exactes en π, intapables
 *       → drag), formule_h_saisie (case inverse, ENTIER tapable), formule_relier,
 *       rect_tableau_saisie, rect_qcm_unique
 *   - graphique (Ex.3c/Ex.4c-e, premier usage placer_points_2d) :
 *       graphique_placer_2d, graphique_constat_qcm (conclusion SÉPARÉE),
 *       graphique_lecture_saisie, graphique_vf — 3 sous-familles (linéaire /
 *       affine / carré) pour que la conclusion varie
 *   - tshirts (Ex.5, prop vs non-prop) : tshirt_tableau_trous_saisie,
 *       tshirt_placer_2d (série normale en dejaPlaces), tshirt_qcm_unique,
 *       tshirt_vf — énoncé VERROUILLÉ : « hors lot complet = prix normal »
 *
 * Invariants : toute l'arithmétique décimale est en DIXIÈMES entiers (exacte) ;
 * les valeurs en π restent symboliques (jamais de flottant ×3,14) ; les
 * coordonnées placer_points_2d sont entières, sur graduations, format canonique
 * "x;y". Variantes estimées : > 2000.
 */

import {
    randInt, randomChoice, randomChoiceMultiple, shuffle,
    basePayload, makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou, makeRelier,
    filtrerDistracteurs,
} from '../../../utils';

const ATTENDU_ID = 993;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ═══════════════════════════════════════════════════════════
// HELPERS (dixièmes entiers, tableaux d'énoncé, repère 2D)
// ═══════════════════════════════════════════════════════════

/** Dixièmes entiers → string français ("35" dixièmes → "3,5"). */
const fmtT = (t) => {
    const neg = t < 0 ? '-' : '';
    const a = Math.abs(t);
    const int = Math.trunc(a / 10);
    const dec = a % 10;
    return dec === 0 ? `${neg}${int}` : `${neg}${int},${dec}`;
};

/** Centièmes entiers → string français ("625" centièmes → "6,25"). */
const fmtC = (c) => {
    const int = Math.trunc(c / 100);
    const rest = c % 100;
    if (rest === 0) return String(int);
    if (rest % 10 === 0) return `${int},${rest / 10}`;
    return `${int},${String(rest).padStart(2, '0')}`;
};

const T_TH = 'background:#1E3A8A;color:#fff;padding:6px 14px;border:1px solid #64748b;font-weight:600';
const T_TD = 'padding:6px 14px;border:1px solid #64748b;text-align:center';
const ligneTable = (th, cells) => `<tr><th style="${T_TH}">${th}</th>`
    + cells.map((c) => `<td style="${T_TD}">${c}</td>`).join('') + '</tr>';
const tableEnonce = (...lignes) =>
    `<table style="border-collapse:collapse;margin:12px auto">${lignes.join('')}</table>`;

/** Payload placer_points_2d — format canonique STRICT "x;y" (§9bis FORMATS_TANOED). */
const makePlacer2D = (texte, repere, points, label, dejaPlaces = null) => {
    const bonne = {};
    points.forEach((p) => { bonne[p.label] = `${fmtT(p.x10)};${fmtT(p.y10)}`; });
    const contenu = {
        subType: 'placer_points_2d',
        repere,
        pointsToPlace: points.map((p) => p.label),
        bonne_reponse: JSON.stringify(bonne),
    };
    if (dejaPlaces && dejaPlaces.length) contenu.dejaPlaces = dejaPlaces;
    return basePayload('association', texte, contenu, opts(label));
};

// ═══════════════════════════════════════════════════════════
// 1. CARRE — périmètre et aire (Ex.1)
// ═══════════════════════════════════════════════════════════

// Côtés en dixièmes : entiers 2..12 + demis (P = 4c entier ; A exacte en centièmes)
const COTES10 = [20, 30, 40, 50, 60, 70, 80, 90, 100, 110, 120, 25, 35, 45, 55, 65];
const perim10 = (c10) => 4 * c10;               // dixièmes
const aire100 = (c10) => c10 * c10;             // centièmes

const genCarreTableauTrous = () => {
    const cotes = randomChoiceMultiple(COTES10, 4).sort((a, b) => a - b);
    // 3 trous : 2 périmètres + 1 aire (côtés distincts pour éviter les échanges équivalents)
    const [iP1, iP2, iA] = shuffle([0, 1, 2, 3]).slice(0, 3);
    const tableau = tableEnonce(
        ligneTable('Côté c (cm)', cotes.map(fmtT)),
        ligneTable('Périmètre P (cm)', cotes.map((c, i) => (i === iP1 || i === iP2 ? '…' : fmtT(perim10(c))))),
        ligneTable('Aire A (cm²)', cotes.map((c, i) => (i === iA ? '…' : fmtC(aire100(c))))),
    );
    const phrase = `Pour c = ${fmtT(cotes[iP1])} cm, P = {{${fmtT(perim10(cotes[iP1]))}}} cm. `
        + `Pour c = ${fmtT(cotes[iP2])} cm, P = {{${fmtT(perim10(cotes[iP2]))}}} cm. `
        + `Pour c = ${fmtT(cotes[iA])} cm, A = {{${fmtC(aire100(cotes[iA]))}}} cm².`;
    return makeTexteTrou(
        phrase,
        `<p>On étudie des carrés de côté c. Le périmètre est donné par P = 4 × c et l'aire par A = c × c. Complète le tableau ci-dessous.</p>\n${tableau}`,
        { mode: 'saisie', ...opts('Compléter le tableau périmètre/aire du carré') },
    );
};

const genCarreSaisie = () => {
    const c10 = randomChoice(COTES10);
    if (randInt(0, 1) === 0) {
        return makeSaisie(fmtT(perim10(c10)),
            `<p>Un carré a un côté de ${fmtT(c10)} cm. En utilisant la formule P = 4 × c, calcule son périmètre.</p>`,
            { ...opts('Calculer le périmètre avec la formule'), suffixe: 'cm' });
    }
    return makeSaisie(fmtC(aire100(c10)),
        `<p>Un carré a un côté de ${fmtT(c10)} cm. En utilisant la formule A = c × c, calcule son aire.</p>`,
        { ...opts('Calculer l’aire avec la formule'), suffixe: 'cm²' });
};

const genCarreQcm = () => {
    if (randInt(0, 1) === 0) {
        // Variante calcul : l'aire (piège : confusion avec le périmètre)
        const c10 = randomChoice(COTES10.filter((c) => c % 10 === 0)); // entiers pour des choix nets
        const c = c10 / 10;
        const bonne = String(c * c);
        const dist = [String(4 * c), String(2 * c), String(c * c + c)]
            .filter((d) => d !== bonne);
        return makeQcm([bonne, ...new Set(dist)].slice(0, 4), bonne,
            `<p>Quelle est l’aire (en cm²) d’un carré de côté ${c} cm ?</p>`,
            opts('Choisir l’aire du carré'));
    }
    // Variante justification : l'aire n'est PAS proportionnelle au côté
    const a = randomChoice([2, 3, 4, 5, 6, 7, 8]);
    const b = 2 * a;
    const bonne = `Non : quand le côté passe de ${a} à ${b} cm, l’aire passe de ${a * a} à ${b * b} cm² — elle est multipliée par 4, pas par 2`;
    const dist = [
        `Oui : quand le côté double, l’aire double aussi`,
        `Oui : l’aire vaut toujours 4 fois le côté`,
        `Non : l’aire ne change pas quand le côté change`,
    ];
    const formulation = randomChoice([
        `<p>L’aire d’un carré est-elle proportionnelle à son côté ? (Tu peux comparer les côtés ${a} cm et ${b} cm.)</p>`,
        `<p>Un carré a un côté de ${a} cm ; un autre a un côté de ${b} cm. L’aire d’un carré est-elle proportionnelle à son côté ?</p>`,
    ]);
    return makeQcm([bonne, ...dist], bonne, formulation,
        opts('Justifier : aire et proportionnalité'));
};

const genCarreVF = () => {
    const a = randomChoice([2, 3, 4, 5]);
    const b = 2 * a;
    const pool = [
        [`Le périmètre d’un carré est proportionnel à son côté`, 0],
        [`L’aire d’un carré est proportionnelle à son côté`, 1],
        [`Quand le côté passe de ${a} cm à ${b} cm, le périmètre passe de ${4 * a} cm à ${4 * b} cm`, 0],
        [`Quand le côté passe de ${a} cm à ${b} cm, l’aire passe de ${a * a} cm² à ${2 * a * a} cm²`, 1],
        [`Pour un carré de côté ${a} cm, la formule P = 4 × c donne P = ${4 * a} cm`, 0],
        [`Le périmètre d’un carré est proportionnel à son aire`, 1],
    ];
    const lignes = randomChoiceMultiple(pool, 4);
    return makeVraiFaux(lignes.map((l) => l[0]), lignes.map((l) => l[1]),
        `<p>Un carré a un côté c, un périmètre P = 4 × c et une aire A = c × c. Vrai ou faux ?</p>`,
        opts('Vrai/faux : périmètre, aire et proportionnalité'));
};

// ═══════════════════════════════════════════════════════════
// 2. FORMULE — cylindre (π symbolique) et rectangles (Ex.3, Ex.4)
// ═══════════════════════════════════════════════════════════

const RAYONS = [2, 3, 4, 5, 6, 7]; // coefficient r² : 4, 9, 16, 25, 36, 49

const genFormuleQcm = () => {
    const r = randomChoice(RAYONS);
    const k = r * r;
    const bonne = `v = ${k}πh`;
    // ⚠ r = 2 : 2r = r² — dédoublonner contre la bonne réponse (collision réelle vue en QA)
    const dist = [...new Set([`v = ${2 * r}πh`, `v = ${r}πh`, `v = ${k}πh²`, `v = ${k + 1}πh`])]
        .filter((d) => d !== bonne)
        .slice(0, 3);
    const formulation = randomChoice([
        `<p>On considère des cylindres de rayon ${r} cm et de hauteur h. Le volume d’un cylindre est V = π × r × r × h. Exprime le volume v en fonction de h.</p>`,
        `<p>Des cylindres ont tous un rayon de ${r} cm ; leur hauteur h varie. Sachant que V = π × r × r × h, quelle formule donne leur volume v en fonction de h ?</p>`,
        `<p>Un vase cylindrique a un rayon de ${r} cm. En utilisant V = π × r × r × h, exprime son volume v en fonction de sa hauteur h.</p>`,
        `<p>Pour des boîtes cylindriques de rayon ${r} cm, exprime le volume v en fonction de la hauteur h (on rappelle : V = π × r × r × h).</p>`,
    ]);
    return makeQcm([bonne, ...dist], bonne, formulation,
        opts('Choisir la formule du volume'));
};

const genFormuleTrouDrag = () => {
    const r = randomChoice(RAYONS);
    const k = r * r;
    const hs = randomChoiceMultiple([2, 3, 4, 6, 8, 10], 2).sort((a, b) => a - b);
    const bons = hs.map((h) => `${k * h}π`);
    const candidats = [`${k * hs[0] + k}π`, `${k + hs[0]}π`, `${2 * k * hs[1]}π`, `${k * hs[1] - k}π`];
    const phrase = `Pour h = ${hs[0]} cm, v = {{${bons[0]}}} cm³. Pour h = ${hs[1]} cm, v = {{${bons[1]}}} cm³.`;
    const distracteurs = filtrerDistracteurs(phrase, candidats).slice(0, 3);
    return makeTexteTrou(
        phrase,
        `<p>Des cylindres ont un rayon de ${r} cm : leur volume est v = ${k}πh (h en cm, v en cm³). Complète avec les valeurs EXACTES.</p>`,
        { mode: 'drag', distracteurs, ...opts('Compléter le tableau du cylindre (valeurs exactes)') },
    );
};

const genFormuleHSaisie = () => {
    const r = randomChoice(RAYONS);
    const k = r * r;
    const h = randInt(2, 12);
    return makeSaisie(String(h),
        `<p>Des cylindres ont un rayon de ${r} cm : leur volume est v = ${k}πh. Un de ces cylindres a un volume v = ${k * h}π cm³. Quelle est sa hauteur h, en cm ?</p>`,
        { ...opts('Retrouver la hauteur (case inverse)'), suffixe: 'cm' });
};

const genFormuleRelier = () => {
    // Refonte bilan prof D3 (2026-07-27) : l'ancien 3↔3 trié se résolvait par
    // tri croissant (le volume croît avec h). Gabarit 2 sources / 4 cibles,
    // sources NON triées, + 2 leurres ancrés chacun sur une source :
    //   · substitution_brute : « v = kπh cm³ » avec h remplacé TEXTUELLEMENT,
    //     non calculé (ex. 49π2) — écriture volontairement mal formée (chiffre
    //     après la lettre ≠ produit), c'est la production exacte de l'élève qui
    //     substitue sans effectuer ; texte plat pour éviter toute lecture
    //     « exposant » ;
    //   · additif : v = (k+h)π — il additionne au lieu de multiplier.
    // Filtres : aucun leurre égal à un volume correct ; cibles homogènes
    // « v = …π cm³ ».
    const r = randomChoice(RAYONS);
    const k = r * r;
    const hs = shuffle(randomChoiceMultiple([2, 3, 4, 5, 6, 8, 10], 2));
    const pairs = hs.map((h) => ({ source: `h = ${h} cm`, target: `v = ${k * h}π cm³` }));

    const volumes = new Set(hs.map((h) => k * h));
    const prisTxt = new Set(pairs.map((p) => p.target));
    const leurres = [];
    const [hSub, hAdd] = shuffle([hs[0], hs[1]]);
    // substitution_brute — jamais égale à un volume correct par forme (« 49π2 »)
    const sub = `v = ${k}π${hSub} cm³`;
    if (!prisTxt.has(sub)) {
        prisTxt.add(sub);
        leurres.push({ target: sub, code: 'substitution_brute' });
    }
    // additif — filtré contre les volumes corrects ((k+h)π ≠ k·h'π)
    for (const h of [hAdd, hSub]) {
        const vAdd = k + h;
        const txt = `v = ${vAdd}π cm³`;
        if (volumes.has(vAdd) || prisTxt.has(txt)) continue;
        prisTxt.add(txt);
        leurres.push({ target: txt, code: 'additif' });
        break;
    }

    const payload = makeRelier(pairs,
        `<p>Des cylindres ont un rayon de ${r} cm : leur volume est v = ${k}πh. Relie chaque hauteur au volume exact correspondant. `
        + 'Attention : certaines étiquettes ne correspondent à aucune hauteur.</p>',
        { ...opts('Relier hauteur et volume exact'), cibles_leurres: leurres });
    payload._meta.params = {
        r, k, hs,
        distracteurs: Object.fromEntries(leurres.map((l) => [l.target, l.code])),
    };
    return payload;
};

// Rectangles de largeur fixe (Ex.4) — aires exactes en dixièmes
const LONGUEURS10 = [10, 20, 25, 30, 45, 50, 62, 70]; // 1 ; 2 ; 2,5 ; 3 ; 4,5 ; 5 ; 6,2 ; 7

const genRectTableauSaisie = () => {
    const k = randomChoice([2, 3, 4]);
    const ls = randomChoiceMultiple(LONGUEURS10, 4).sort((a, b) => a - b);
    const [t1, t2] = shuffle([0, 1, 2, 3]).slice(0, 2);
    const tableau = tableEnonce(
        ligneTable('Longueur L (cm)', ls.map(fmtT)),
        ligneTable('Aire S (cm²)', ls.map((l, i) => (i === t1 || i === t2 ? '…' : fmtT(k * l)))),
    );
    const phrase = `Pour L = ${fmtT(ls[t1])} cm, S = {{${fmtT(k * ls[t1])}}} cm². `
        + `Pour L = ${fmtT(ls[t2])} cm, S = {{${fmtT(k * ls[t2])}}} cm².`;
    return makeTexteTrou(
        phrase,
        `<p>Des rectangles ont tous une largeur de ${k} cm. Leur aire est S = ${k} × L. Complète le tableau ci-dessous.</p>\n${tableau}`,
        { mode: 'saisie', ...opts('Compléter le tableau des aires de rectangles') },
    );
};

const genRectQcm = () => {
    const k = randomChoice([2, 3, 4, 5, 6, 7]);
    const ex = randomChoice([2, 3, 4, 5]);
    const bonne = `Oui : l’aire s’obtient toujours en multipliant L par ${k}`;
    const dist = [
        `Non : l’aire ne dépend pas de L`,
        `Oui : l’aire s’obtient en ajoutant ${k} à L`,
        `Non : quand L double, l’aire est multipliée par 4`,
    ];
    const formulation = randomChoice([
        `<p>Des rectangles ont tous une largeur de ${k} cm et une aire S = ${k} × L (par exemple, S = ${k * ex} cm² pour L = ${ex} cm). L’aire est-elle proportionnelle à la longueur L ?</p>`,
        `<p>On étudie des rectangles de largeur fixe ${k} cm : leur aire est S = ${k} × L. Pour L = ${ex} cm, S = ${k * ex} cm². S est-elle proportionnelle à L ?</p>`,
    ]);
    return makeQcm([bonne, ...dist], bonne, formulation,
        opts('Aire du rectangle et proportionnalité'));
};

// ═══════════════════════════════════════════════════════════
// 3. GRAPHIQUE — du tableau au repère (placer_points_2d)
// ═══════════════════════════════════════════════════════════

// Trois sous-familles pour que la conclusion varie
const tirerSituationGraphique = () => {
    const kind = randomChoice(['lineaire', 'affine', 'carre']);
    if (kind === 'lineaire') {
        const k = randomChoice([2, 3, 4, 5]);
        const ctx = randomChoice([
            { nom: 'le prix des cerises', x: 'masse (kg)', y: 'prix (€)', formule: `y = ${k} × x` },
            { nom: 'la distance parcourue à vitesse constante', x: 'durée (min)', y: 'distance (km)', formule: `y = ${k} × x` },
            { nom: 'l’aire de rectangles de largeur fixe', x: 'longueur L (cm)', y: 'aire S (cm²)', formule: `y = ${k} × x` },
        ]);
        const xs = randomChoiceMultiple([1, 2, 3, 4, 5, 6], 3).sort((a, b) => a - b);
        return { kind, k, b: 0, ctx, xs, ys: xs.map((x) => k * x) };
    }
    if (kind === 'affine') {
        const k = randomChoice([1, 2, 3]);
        const b = randomChoice([2, 3, 4]);
        const fm = k === 1 ? `y = x + ${b}` : `y = ${k} × x + ${b}`;
        const ctx = randomChoice([
            { nom: 'le prix d’une livraison (frais fixes + prix au kg)', x: 'masse (kg)', y: 'prix (€)', formule: fm },
            { nom: 'le coût d’une location (forfait + prix par heure)', x: 'durée (h)', y: 'coût (€)', formule: fm },
        ]);
        const xs = randomChoiceMultiple([1, 2, 3, 4, 5, 6], 3).sort((a, b2) => a - b2);
        return { kind, k, b, ctx, xs, ys: xs.map((x) => k * x + b) };
    }
    const ctx = { nom: 'l’aire de carrés', x: 'côté c (cm)', y: 'aire A (cm²)', formule: 'y = x × x' };
    const xs = randomChoiceMultiple([1, 2, 3, 4, 5, 6], 3).sort((a, b) => a - b);
    return { kind, k: 1, b: 0, ctx, xs, ys: xs.map((x) => x * x) };
};

/** Formule + correspondance x/y ↔ grandeurs (les colonnes du tableau sont nommées). */
const formuleAff = (sit) =>
    `${sit.ctx.formule}, où x est « ${sit.ctx.x} » et y « ${sit.ctx.y} »`;

const tableSituation = (sit, avecPoints = false) => {
    const lignes = [
        ligneTable(sit.ctx.x, sit.xs.map(String)),
        ligneTable(sit.ctx.y, sit.ys.map(String)),
    ];
    if (avecPoints) lignes.unshift(ligneTable('Point', sit.xs.map((_, i) => 'ABC'[i])));
    return tableEnonce(...lignes);
};

const repereDe = (sit) => {
    const maxY = Math.max(...sit.ys);
    return {
        xMax: Math.max(...sit.xs) + 1,
        yMax: Math.min(40, Math.ceil((maxY + 1) / 5) * 5),
        xStep: 1,
        yStep: 1,
        xLabel: sit.ctx.x,
        yLabel: sit.ctx.y,
        grille: true,
    };
};

const genGraphiquePlacer2D = () => {
    const sit = tirerSituationGraphique();
    const points = sit.xs.map((x, i) => ({ label: 'ABC'[i], x10: x * 10, y10: sit.ys[i] * 10 }));
    return makePlacer2D(
        `<p>Ce tableau de valeurs décrit ${sit.ctx.nom} (formule : ${formuleAff(sit)}).</p>\n${tableSituation(sit, true)}\n<p>Place les points A, B et C dans le repère.</p>`,
        repereDe(sit), points, 'Placer les points du tableau dans le repère',
    );
};

const genGraphiqueConstatQcm = () => {
    const sit = tirerSituationGraphique();
    const reponses = {
        lineaire: 'Les points sont alignés entre eux ET avec l’origine : la situation est proportionnelle',
        affine: 'Les points sont alignés entre eux mais PAS avec l’origine : la situation n’est pas proportionnelle',
        carre: 'Les points ne sont pas alignés : la situation n’est pas proportionnelle',
    };
    const bonne = reponses[sit.kind];
    const choix = [reponses.lineaire, reponses.affine, reponses.carre];
    return makeQcm(choix, bonne,
        `<p>On a placé dans un repère les points de ce tableau (${sit.ctx.nom}, formule ${formuleAff(sit)}).</p>\n${tableSituation(sit)}\n<p>Que constate-t-on ?</p>`,
        opts('Conclure après avoir placé les points'));
};

const genGraphiqueLectureSaisie = () => {
    const sit = tirerSituationGraphique();
    const xq = randomChoice([1, 2, 3, 4, 5, 6, 7].filter((x) => !sit.xs.includes(x)));
    const yq = sit.kind === 'carre' ? xq * xq : sit.k * xq + sit.b;
    return makeSaisie(String(yq),
        `<p>Ce tableau décrit ${sit.ctx.nom}, avec la formule ${formuleAff(sit)}.</p>\n${tableSituation(sit)}\n<p>En utilisant la formule, calcule y pour x = ${xq}.</p>`,
        opts('Calculer une nouvelle valeur avec la formule'));
};

const genGraphiqueVF = () => {
    const sit = tirerSituationGraphique();
    const x1 = sit.xs[0];
    const y1 = sit.ys[0];
    const yDouble = sit.kind === 'carre' ? 4 * y1 : 2 * sit.k * x1 + sit.b;
    const pool = [
        ['La situation est proportionnelle', sit.kind === 'lineaire' ? 0 : 1],
        ['Le point (0 ; 0) appartient à la représentation graphique', sit.kind === 'affine' ? 1 : 0],
        [`Quand x passe de ${x1} à ${2 * x1}, y passe de ${y1} à ${2 * y1}`, yDouble === 2 * y1 ? 0 : 1],
        [`Pour x = ${x1}, y = ${y1}`, 0],
        [`Pour x = ${sit.xs[1]}, y = ${sit.ys[1] + randomChoice([1, 2])}`, 1],
    ];
    const lignes = randomChoiceMultiple(pool, 4);
    return makeVraiFaux(lignes.map((l) => l[0]), lignes.map((l) => l[1]),
        `<p>Ce tableau décrit ${sit.ctx.nom} (formule ${formuleAff(sit)}).</p>\n${tableSituation(sit)}\n<p>Vrai ou faux ?</p>`,
        opts('Vrai/faux sur un tableau de valeurs'));
};

// ═══════════════════════════════════════════════════════════
// 4. TSHIRTS — tarif normal vs tarif soldé (Ex.5)
// ═══════════════════════════════════════════════════════════

const ARTICLES = [
    { sing: 'tee-shirt', plur: 'tee-shirts' },
    { sing: 'mug', plur: 'mugs' },
    { sing: 'cahier', plur: 'cahiers' },
    { sing: 'porte-clés', plur: 'porte-clés' },
];

const tirerTarif = () => {
    const u = randomChoice([4, 5]);           // prix unitaire (u = 6 dépasserait yMax 40)
    const promo = 3 * u - randomChoice([2, 3, 4]); // prix du lot de 3
    const article = randomChoice(ARTICLES);
    const solde = (n) => promo * Math.trunc(n / 3) + u * (n % 3);
    return { u, promo, article, solde };
};

const CONSIGNE_LOT = (t) =>
    `<p>Dans un magasin, un ${t.article.sing} coûte ${t.u} € au prix normal. En promotion, le lot de 3 ${t.article.plur} coûte ${t.promo} €. Les articles qui ne forment pas un lot complet restent au prix normal.</p>`;

const genTshirtTableauTrous = () => {
    const t = tirerTarif();
    const ns = [1, 2, 3, 4, 5, 6];
    const trous = randomChoiceMultiple([2, 3, 4, 5], 3).sort((a, b) => a - b);
    const tableau = tableEnonce(
        ligneTable('Nombre acheté', ns.map(String)),
        ligneTable('Prix normal (€)', ns.map((n) => String(t.u * n))),
        ligneTable('Prix soldé (€)', ns.map((n) => (trous.includes(n) ? '…' : String(t.solde(n))))),
    );
    const phrase = trous
        .map((n) => `Pour ${n} articles, le prix soldé est {{${t.solde(n)}}} €.`)
        .join(' ');
    return makeTexteTrou(
        phrase,
        `${CONSIGNE_LOT(t)}\n${tableau}\n<p>Complète la ligne du prix soldé.</p>`,
        { mode: 'saisie', ...opts('Compléter le tableau des deux tarifs') },
    );
};

const genTshirtPlacer2D = () => {
    const t = tirerTarif();
    const nsPlaces = randomChoiceMultiple([2, 3, 4, 5, 6], 3).sort((a, b) => a - b);
    const dejaPlaces = [1, 2, 3, 4, 5, 6].map((n) => ({ x: n, y: t.u * n }));
    const maxY = Math.max(t.u * 6, t.solde(6));
    const repere = {
        xMax: 7, yMax: Math.min(40, Math.ceil((maxY + 1) / 5) * 5),
        xStep: 1, yStep: 1,
        xLabel: 'nombre acheté', yLabel: 'prix (€)', grille: true,
    };
    const points = nsPlaces.map((n, i) => ({ label: 'ABC'[i], x10: n * 10, y10: t.solde(n) * 10 }));
    const legende = nsPlaces.map((n, i) => `${'ABC'[i]} pour ${n} articles`).join(', ');
    return makePlacer2D(
        `${CONSIGNE_LOT(t)}\n<p>Les points gris représentent le prix NORMAL. Place les points du prix SOLDÉ : ${legende}.</p>`,
        repere, points, 'Placer les points du tarif soldé', dejaPlaces,
    );
};

const genTshirtQcm = () => {
    const t = tirerTarif();
    const bonne = `Le prix normal : il vaut toujours ${t.u} × le nombre acheté`;
    const dist = [
        'Le prix soldé : il est plus avantageux',
        'Les deux tarifs',
        'Aucun des deux tarifs',
    ];
    return makeQcm([bonne, ...dist], bonne,
        `${CONSIGNE_LOT(t)}\n<p>Lequel de ces deux tarifs est proportionnel au nombre d’articles achetés ?</p>`,
        opts('Quel tarif est proportionnel ?'));
};

const genTshirtVF = () => {
    const t = tirerTarif();
    const pool = [
        ['Le prix normal est proportionnel au nombre d’articles', 0],
        ['Le prix soldé est proportionnel au nombre d’articles', 1],
        [`6 articles soldés coûtent le double de 3 articles soldés`, 0],
        [`4 articles soldés coûtent ${t.solde(4)} €`, 0],
        [`5 articles soldés coûtent ${5 * t.u} €`, t.solde(5) === 5 * t.u ? 0 : 1],
        [`2 articles coûtent ${2 * t.u} €, au prix normal comme au prix soldé`, 0],
    ];
    const lignes = randomChoiceMultiple(pool, 4);
    return makeVraiFaux(lignes.map((l) => l[0]), lignes.map((l) => l[1]),
        `${CONSIGNE_LOT(t)}\n<p>Vrai ou faux ?</p>`,
        opts('Vrai/faux sur les deux tarifs'));
};

// ═══════════════════════════════════════════════════════════
// Registre + contrat générateur standard
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    carre_tableau_trous_saisie: genCarreTableauTrous,
    carre_texte: genCarreSaisie,
    carre_qcm_unique: genCarreQcm,
    carre_vf: genCarreVF,

    formule_qcm_unique: genFormuleQcm,
    formule_trou_drag: genFormuleTrouDrag,
    formule_h_saisie: genFormuleHSaisie,
    formule_relier: genFormuleRelier,
    rect_tableau_trous_saisie: genRectTableauSaisie,
    rect_qcm_unique: genRectQcm,

    graphique_placer_2d: genGraphiquePlacer2D,
    graphique_constat_qcm: genGraphiqueConstatQcm,
    graphique_lecture_saisie: genGraphiqueLectureSaisie,
    graphique_vf: genGraphiqueVF,

    tshirt_tableau_trous_saisie: genTshirtTableauTrous,
    tshirt_placer_2d: genTshirtPlacer2D,
    tshirt_qcm_unique: genTshirtQcm,
    tshirt_vf: genTshirtVF,
};

const FORMAT_LABELS = {
    tableau_trous_saisie: 'Compléter le tableau',
    texte: 'Saisie',
    qcm_unique: 'QCM',
    vf: 'Vrai/Faux',
    trou_drag: 'Compléter (glisser)',
    h_saisie: 'Case inverse (saisie)',
    relier: 'Relier',
    placer_2d: 'Placer dans le repère',
    constat_qcm: 'Conclure (QCM)',
    lecture_saisie: 'Calculer une valeur',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS)
        .filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({
            key: k,
            label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k,
        }));

export const CATEGORIES = [
    {
        id: 'carre',
        label: 'Périmètre et aire du carré (tableau de valeurs)',
        formatsList: buildFormats('carre'),
    },
    {
        id: 'formule',
        label: 'Du tableau à la formule (cylindre, rectangles)',
        formatsList: [...buildFormats('formule'), ...buildFormats('rect')],
    },
    {
        id: 'graphique',
        label: 'Du tableau de valeurs au graphique',
        formatsList: buildFormats('graphique'),
    },
    {
        id: 'tshirts',
        label: 'Deux tarifs : proportionnel ou pas',
        formatsList: buildFormats('tshirt'),
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length
            ? randomChoice(matching)
            : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
